/**!
 *  Job Specializations widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from 'react';
import Widget from '../../widget.js';
import styles from './jobspecialization.module.scss';
import API from 'Class/API';
import Fuse from 'Class/Fuse';
import Collapsable from 'Components/Layout/Collapsable';
import Icon from 'Components/Layout/Icon';
import Item from 'Components/UI/Item';
import Link from 'Components/UI/Link';
import Preview from 'Components/Layout/Preview'
import Spinner from 'Components/Feedback/Spinner';
import WidgetWrapper from 'Components/UI/WidgetWrapper';
import TextField from 'Components/UI/Field/TextField';

class WidgetJobSpecializations extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: 'Job Specializations',
                insert: true,
                label: 'Title',
                reset: true,
                type: 'text',
                translate: true
            }
        }, {
            backgroundColor: 'transparent',
            textColor: 'black'
        }, true);
        this.FilterTimeout = null;
        this.LoadingUsers = {};
        this.Mounted = false;
        this.Name = 'Job Specializations';
        this.Translate = {
            loading: 'Loading specializations...',
            nocontent: 'No specializations found',
            search: 'Search for specializations...'
        };
        this.state =
        {
            error: false,
            expand: {},
            filter: '',
            list: [],
            loading: false
        };
    }

    /**
     * Load activities on mount.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        this.Reload();
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    /**
     * Render specialization item.
     * @return JSX.Element - Specialization item element.
     */

    Item = ({activities = [], id, name}, index) =>
    {
        const {expand} = this.state;
        return (
            <div className={styles.item} key={index}>
                <Item
                    className={[styles.toggle, expand[id] ? styles.expanded : ''].join(' ')}
                    id={id}
                    onClick={() => this.setState({expand: {...expand, [id]: !expand[id]}})}
                >
                    {name}
                    <Icon className={styles.plus} feather="Plus"/>
                </Item>
                <Collapsable collapsed={!expand[id]}>
                    <div className={styles.activities}>
                        {activities.map((activity, index) => this.SubItem(activity, index))}
                    </div>
                </Collapsable>
            </div>
        );
    }

    /**
     * Load activities.
     * @return void
     */

    Load = () =>
    {
        const {filter} = this.state;
        const {contextId} = this.props;
        this.setState({
            error: false,
            list: [],
            loading: true
        });
        API.Request('skillActivities/specialization-list', {
            community: contextId,
            filter
        }, async (response) =>
        {
            if (!this.Mounted)
            {
                return;
            }
            const {list} = response;
            if (!list)
            {
                this.setState({error: true, loading: false});
            }
            else {
                this.setState({list, loading: false});
            }
        });
    }

    /**
     * Update search filter and load feed after a timeout
     * to avoid hammering the API.
     * @return void
     */

    OnFilter = (_, filter) =>
    {
        clearTimeout(this.FilterTimeout);
        this.setState({filter, loading: true, list: []});
        this.FilterTimeout = setTimeout(() =>
        {
            this.Load();
        }, 300);
    }

    /**
     * Reload activities.
     * @return void
     */

    Reload = () =>
    {
        this.Load();
    }
    
    /**
     * Render activity item.
     * @return JSX.Element - Activity item element.
     */

    SubItem = ({description = '', image, name = '', topic}, index) =>
    {
        const topicId = Array.isArray(topic) ? topic[0] : topic;
        const link = topicId ? Fuse.ContentUrl(topicId, 'topic') : null;
        return (
            <Link
                key={index}
                className={styles.subItem}
                href={link}
                target='_blank'
            >
                <Preview
                    className={styles.subItemPreview}
                    content='activity'
                    image={image || null}
                    overlay={false}
                />
                <div className={styles.subItemContent}>
                    <div className={styles.subItemTop}>{name}</div>
                    <div className={styles.subItemInfo}>{description}</div>
                </div>
            </Link>
        )
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, hover} = this.props;
        const {list, loading} = this.state;
        const {title} = WidgetContent;
        const CA = this.ClassNames([styles.widget]);
        if (active)
        {
            CA.push('Active');
        }
        if (hover)
        {
            CA.push('Hover'); 
        }
        const Content = [];
        const Items = [];
        if (list && list.length)
        {
            list.forEach((c, i) =>
            {
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className='WidgetSpinner'
                key='spinner'
                message={this.GetTranslation('loading')}
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className='WidgetEmpty'
                key='empty'
            >{this.GetTranslation('nocontent')}</div>);
        }
        else
        {
            Content.push(<div
                className={styles.items}
                key='items'
            >{Items}</div>);
        }
        return (
            <div className={CA.join(' ')} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className={styles.wrapper}
                    onScrollView={this.OnScrollView}
                    title={title}
                    toolbar={<TextField
                        feather='Search'
                        onChange={this.OnFilter}
                        onInput={this.OnFilter}
                        placeholder={this.GetTranslation('search')}
                    />}
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetJobSpecializations;